/**
 * Утилиты для форматирования данных
 */

/**
 * Форматирует число с разделителями тысяч
 * @param num - число для форматирования
 * @param decimals - количество знаков после запятой (по умолчанию 2)
 * @returns отформатированная строка
 */
export const formatNumber = (num: number | undefined, decimals: number = 1): string => {
  // Проверяем только на undefined, null, не число или NaN
  if (num === undefined || num === null || typeof num !== 'number' || isNaN(num)) return '0';

  // Если ноль - показываем '0'
  if (num === 0) return '0';

  // Для очень маленьких чисел (< 0.01) используем адаптивную точность
  // Чтобы показать значимые цифры вместо 0.0
  if (num > 0 && num < 0.01) {
    // Находим первую значимую цифру
    let adaptiveDecimals = 2;
    let tempNum = num;
    while (tempNum < 1 && adaptiveDecimals < 10) {
      tempNum *= 10;
      adaptiveDecimals++;
    }
    // Используем экспоненциальную нотацию для ОЧЕНЬ маленьких чисел
    if (adaptiveDecimals > 8) {
      return num.toExponential(2);
    }
    return num.toLocaleString('ru-RU', {
      minimumFractionDigits: adaptiveDecimals,
      maximumFractionDigits: adaptiveDecimals
    });
  }

  // Всегда показываем число с разделителями тысяч
  // Если запрошены десятичные знаки (decimals > 0), всегда показываем их
  // Это важно для балансов - чтобы пользователь видел точную сумму (52.95 а не 53)
  if (decimals > 0) {
    return num.toLocaleString('ru-RU', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  // Иначе показываем целое число с разделителями
  return num.toLocaleString('ru-RU', { maximumFractionDigits: 0 });
};

/**
 * Форматирует время в формате HH:MM:SS
 * @param seconds - количество секунд
 * @param lang - язык (ru/en), по умолчанию en
 * @returns отформатированная строка времени
 */
export const formatTime = (seconds: number, lang: string = 'en'): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  const isRu = lang === 'ru';

  if (hours > 0) {
    return isRu ? `${hours}ч ${minutes}м ${secs}с` : `${hours}h ${minutes}m ${secs}s`;
  }
  if (minutes > 0) {
    return isRu ? `${minutes}м ${secs}с` : `${minutes}m ${secs}s`;
  }
  return isRu ? `${secs}с` : `${secs}s`;
};

/**
 * Форматирует число как процент без знака % (для использования в переводах)
 * Убирает лишние нули: 1.00 -> 1, 1.10 -> 1.1, 1.05 -> 1.05
 * @param value - значение процента
 * @param decimals - количество знаков после запятой для округления
 * @returns отформатированная строка без %
 */
export const formatPercentValue = (value: number, decimals: number = 2): string => {
  if (typeof value !== 'number' || isNaN(value)) return '0';
  const fixed = value.toFixed(decimals);
  return parseFloat(fixed).toString();
};

/**
 * Форматирует процент
 * Убирает лишние нули: 1.00% -> 1%, 1.10% -> 1.1%, 1.05% -> 1.05%
 * @param value - значение процента
 * @param decimals - количество знаков после запятой для округления
 * @returns отформатированная строка с %
 */
export const formatPercent = (value: number, decimals: number = 2): string => {
  if (typeof value !== 'number' || isNaN(value)) return '0%';
  return `${formatPercentValue(value, decimals)}%`;
};

/**
 * Форматирует дату в относительном формате (2 мин назад, 1 час назад и т.д.) в МСК
 * @param date - дата для форматирования
 * @param lang - язык (ru/en), по умолчанию en
 * @returns отформатированная строка
 */
export const formatRelativeTime = (date: Date | string, lang: string = 'en'): string => {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  const isRu = lang === 'ru';

  if (diffSec < 60) {
    return isRu ? 'только что' : 'just now';
  }
  if (diffMin < 60) {
    return isRu ? `${diffMin} мин назад` : `${diffMin} min ago`;
  }
  if (diffHour < 24) {
    return isRu ? `${diffHour} ч назад` : `${diffHour}h ago`;
  }
  if (diffDay < 7) {
    return isRu ? `${diffDay} дн назад` : `${diffDay}d ago`;
  }

  // Форматируем дату в МСК
  const locale = isRu ? 'ru-RU' : 'en-US';
  return then.toLocaleString(locale, {
    timeZone: 'Europe/Moscow',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).replace(/\//g, '.');
};

/**
 * Форматирует адрес кошелька, скрывая середину
 * @param address - адрес кошелька
 * @param startChars - количество символов в начале
 * @param endChars - количество символов в конце
 * @returns отформатированная строка
 */
export const formatAddress = (address: string, startChars: number = 6, endChars: number = 4): string => {
  if (!address) return '';
  if (address.length <= startChars + endChars) return address;
  
  return `${address.slice(0, startChars)}...${address.slice(-endChars)}`;
};

/**
 * Форматирует имя пользователя, скрывая часть символов
 * @param name - имя пользователя
 * @param lang - язык (ru/en), по умолчанию en
 * @returns отформатированная строка
 */
export const formatUsername = (name: string, lang: string = 'en'): string => {
  if (!name) return lang === 'ru' ? 'Аноним' : 'Anonymous';
  if (name.length <= 3) return name + '***';

  const visiblePart = name.slice(0, Math.min(4, Math.floor(name.length / 2)));
  return `${visiblePart}***`;
};